import React from "react"
import type { EdgeInsets } from "react-native-safe-area-context"

import styled from "styled-components/native"

import { getOptimizedImageSource } from "@treefort/lib/get-optimized-image-source"

import { useTrack } from "../hooks/audio"
import { createStyle } from "../lib/create-style"
import { kebabCase } from "../lib/kebab-case"
import AudioPlayerPlayButton from "./audio-player-play-button"
import { ImageWithFade } from "./image-with-fade"
import Row from "./row"
import Spacer from "./spacer"
import Text from "./text"
import { useTokens } from "./tokens-provider"
import Touchable from "./touchable"

const backgroundContainerStyle = createStyle({
  position: "absolute",
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
})

const Container = styled.View<{ $safeAreaInsets: EdgeInsets }>`
  width: 100%;
  background: ${({ theme }) => theme.colors.background.primary};
  border-color: ${({ theme }) => theme.colors.border.primary};
  border-top-width: 1px;
  height: ${({ theme, $safeAreaInsets: safeAreaInsets }) =>
    theme.audioPlayerInline.height + safeAreaInsets.bottom}px;
  padding-bottom: ${(props) => props.$safeAreaInsets.bottom}px;
`

// This touchable triggers the expand/collapse action. It's absolutely
// positioned so that it can extend behind the other touchables (play/skip) and
// display ripple or highlight feedback accross the entire audio player. It does
// _not_ wrap the other touchables (nesting touchables causes all sorts of
// problems).
const BackgroundTouchable = styled(Touchable)<{ $safeAreaInsets: EdgeInsets }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  padding-left: ${(props) =>
    props.theme.audioPlayerInline.padding + props.$safeAreaInsets.left}px;
  padding-right: ${(props) =>
    props.$safeAreaInsets.right +
    props.theme.audioPlayerInline.padding +
    props.theme.minTapTarget}px;
`

const Metadata = styled.View`
  width: 100%;
  height: 100%;
  flex-direction: row;
  align-items: center;
`

const Artwork = styled(ImageWithFade)<{ $size: number }>`
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  border-radius: ${({ theme }) => theme.borderRadius.rounded}px;
  background-color: ${(props) => props.theme.colors.loading.image};
`

const Title = styled(Text)`
  flex: 1;
`

export default function AudioPlayerInline({
  onPress,
  safeAreaInsets,
}: {
  onPress: () => void
  safeAreaInsets: EdgeInsets
}): React.JSX.Element {
  const track = useTrack()
  const { tokens } = useTokens()

  return (
    <Container $safeAreaInsets={safeAreaInsets}>
      <Row
        alignItems="center"
        justifyContent="flex-end"
        height={tokens.audioPlayerInline.height}
      >
        <BackgroundTouchable
          id={kebabCase(
            `audio-player-inline-background-${track?.album || "unknown-album"}-${track?.title || "unknown-track"}`,
          )}
          $safeAreaInsets={safeAreaInsets}
          onPressIn={onPress}
          feedback="ripple-or-highlight"
          containerStyle={backgroundContainerStyle}
        >
          <Metadata>
            <Artwork
              $size={
                tokens.audioPlayerInline.height -
                2 * tokens.audioPlayerInline.padding
              }
              source={getOptimizedImageSource(
                { uri: track?.artwork },
                // Use the same source image size as the fullscreen player so that
                // we only have to load the image once for both players
                tokens.audioPlayerFullscreen.maxWidth,
              )}
              resizeMode="cover"
            />
            <Spacer size="medium" horizontal={true} />
            <Title textStyle="strong" numberOfLines={1}>
              {track?.title}
            </Title>
          </Metadata>
        </BackgroundTouchable>
        <AudioPlayerPlayButton iconSize="large" />
      </Row>
    </Container>
  )
}
